import { SitesResponse, SlateResponse } from "../../common/store/types";
import { api, IApiOptions } from "../../utils/api/actions";
import { Constants } from "./constants";
import { Filter, Filters, Page, Pages, MultipleFilters, ResultsLoadError, SimulationTimestamp } from "./types";

export const setContest = (contest: SitesResponse) =>({
  type: Constants.SET_CONTEST,
  payload: contest,
});

export const setLineupVerified = (site: boolean) =>({
  type: Constants.SET_LINEUP_VERIFIED,
  payload: site,
});

export const setSimulatedSlate = (slate: SlateResponse) =>({
  type: Constants.SET_SIMULATED_SLATE,
  payload: slate,
});

export const setFilter = (filter: Filter | MultipleFilters , type: Constants.SET_USER_SORT | Constants.SET_PLAYER_SORT | Constants.SET_LINEUP_SORT | Constants.SET_STACK_SORT) => ({
  type,
  payload: filter,
});

export const setPageInfo = (pageInfo: Pages) => ({
  type: Constants.SET_PAGE,
  payload: pageInfo,
});

export const setDisableProgressCheck = (newValue: boolean) => ({
  type: Constants.SET_DISABLE_PROGRESS_CHECK,
  payload: newValue,
});

export const setTotalEntries = (newValue: number) => ({
  type: Constants.SET_TOTAL_ENTRIES,
  payload: newValue,
});
export const setLineupFile = (file: string) => ({
  type: Constants.SET_LINEUP_FILE,
  payload: file,
});
export const setPercentToFirst = (percent: number) => ({
  type: Constants.SET_PERCENT_TO_FIRST,
  payload: percent,
});
export const setSimCountAction = (count: number) => ({
  type: Constants.SET_SIM_COUNT,
  payload: count,
});
export const setSimulationTimestamp = (timestamp: SimulationTimestamp) => ({
  type: Constants.SET_SIMULATION_TIMESTAMP,
  payload: timestamp,
});
export const setShowCachedResultError = (show: boolean) => ({
  type: Constants.SET_SHOW_CACHED_RESULT_ERROR,
  payload: show,
});
export const setResultsLoadError = (error: ResultsLoadError | null) => ({
  type: Constants.SET_RESULTS_LOAD_ERROR,
  payload: error,
});
export const setIsParsingCsv = (isParsing: boolean) => ({
  type: Constants.SET_IS_PARSING_CSV,
  payload: isParsing,
});

export const getPlayerTabRoi = (slateId: string, user: string, opponent: string, filter: Filter, pageInfo: Page) => {
  let options: IApiOptions = {
    method: 'GET',
    route: '',
  };

  const route = '/api/contests/getRoiByPlayer';

  options = {
    ...options,
    route,
    params: {
      slateId,
      user,
      opponent,
      sortBy: filter.sortBy,
      sortOrder: filter.sortOrder,
      position: filter.position,
      currentPage: pageInfo.currentPage.toString(),
      pageSize: pageInfo.pageSize.toString(),
      contestType: 'PreContest',
      includeAllPlayers: true,
    }
  };

  return api(
    Constants.SET_PLAYER_ALL_ROI, {
    ...options,
  }, Constants.SET_PLAYER_ALL_ROI_PAGINATION
  );
};

export const getLineupTabRoi = (slateId: string, user: string, filter: Filter, pageInfo: Page) => {
  let options: IApiOptions = {
    method: 'GET',
    route: '',
    // Every lineup comes back in one response: ~25k lineups is ~14 MB gzipped, which a slow
    // connection can't download in 60s
    timeout: 180000
  };

  const route = '/api/contests/getRoiByLineup';

  options = {
    ...options,
    route,
    params: {
      slateId,
      user,
      sortBy: filter.sortBy,
      sortOrder: filter.sortOrder,
      currentPage: pageInfo.currentPage.toString(),
      pageSize: pageInfo.pageSize.toString(),
      contestType: 'PreContest',
      includeAllLineups: true,
      // Leaves out the late swap fields and every lineupInfo field but position, none of which the pre-contest
      // screens read (about a third of the size). APIs without slim ignore it.
      slim: true,
    }
  };

  return api(
    Constants.SET_LINEUP_ALL_ROI, {
    ...options,
  }, Constants.SET_LINEUP_ALL_ROI_PAGINATION
  );
};

export const getStackTabRoi = (slateId: string, user: string, filter: Filter, pageInfo: Page, sport: string) => {
  let options: IApiOptions = {
    method: 'GET',
    route: '',
  };
  let route = '/api/contests/getRoiByStack';
  if (['nfl', 'cfb'].includes(sport?.toLowerCase())) {
    route = '/api/contests/getRoiByStackNFL';
  }

  options = {
    ...options,
    route,
    params: {
      slateId,
      user,
      sortBy: ['nfl', 'cfb'].includes(sport?.toLowerCase()) ? 'QBName' : filter.sortBy,
      sortOrder: filter.sortOrder,
      currentPage: pageInfo.currentPage.toString(),
      pageSize: pageInfo.pageSize.toString(),
      contestType: 'PreContest',
      includeAllStacks: true,
    }
  };

  return api(
    Constants.SET_STACK_ALL_ROI, {
    ...options,
  }, Constants.SET_STACK_ALL_ROI_PAGINATION
  );
};

export const getContestPlayers = (slateId: string, user: string, opponent: string, filter: Filter, pageInfo: Page) => {
  let options: IApiOptions = {
    method: 'GET',
    route: '',
  };

  const route = '/api/contests/getRoiByPlayer';

  options = {
    ...options,
    route,
    params: {
      slateId,
      user,
      opponent,
      contestType: 'PreContest',
      includeAllPlayers: true,
    }
  };

  return api(
    Constants.SET_CONTEST_PLAYERS, {
    ...options,
  }
  );
};

// Loads every results view for a finished sim. Used when the sim reports done and when the user retries
// a failed load, so both fetch the same views.
export const getSimResults = (slateId: string, user: string, filters: Filters, pageInfo: Pages, sport: string, includeStacks: boolean) =>
  (dispatch: (action: any) => any) => {
    dispatch(getLineupTabRoi(slateId, user, filters.lineup.all, pageInfo.lineup));
    dispatch(getPlayerTabRoi(slateId, user, '', filters.player.all, pageInfo.player));
    dispatch(getContestPlayers(slateId, user, '', filters.player.all, pageInfo.player));
    if (includeStacks) {
      dispatch(getStackTabRoi(slateId, user, filters.stack.all, pageInfo.stack, sport));
    }
  };

export const getSupportedSites = (sport: string) => {
  let options: IApiOptions = {
    method: 'GET',
    route: '',
  };

  const route = '/api/contests/getSupportedSites';

  options = {
    ...options,
    route,
    params: {
      sport,
    }
  };

  return api(
    Constants.SET_SITES, {
    ...options,
  }
  );
};
